/**
 * Common file validation utilities
 * Used across multiple upload components to avoid code duplication
 */

/**
 * Validates if a file type is allowed
 * @param {File} file - The file to validate
 * @param {string[]} allowedTypes - Array of allowed MIME types (e.g., ['image/jpeg', 'image/png'])
 * @returns {boolean} - True if file type is allowed
 */
export const isValidFileType = (file, allowedTypes = []) => {
  if (!file || !allowedTypes.length) return false
  return allowedTypes.includes(file.type)
}

/**
 * Validates file size
 * @param {File} file - The file to validate
 * @param {number} maxSizeInMB - Maximum file size in MB
 * @returns {boolean} - True if file size is within limit
 */
export const isValidFileSize = (file, maxSizeInMB = 10) => {
  if (!file) return false
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024
  return file.size <= maxSizeInBytes
}

/**
 * Validates multiple files
 * @param {File[]} files - Array of files to validate
 * @param {Object} options - Validation options
 * @param {string[]} options.allowedTypes - Allowed MIME types
 * @param {number} options.maxSizeInMB - Maximum file size in MB
 * @returns {Object} - { valid: boolean, validFiles: File[], errors: string[] }
 */
export const validateFiles = (files, options = {}) => {
  const {
    allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'],
    maxSizeInMB = 10,
  } = options

  const validFiles = []
  const errors = []

  Array.from(files).forEach((file) => {
    if (!isValidFileType(file, allowedTypes)) {
      errors.push(
        `${file.name}: Invalid file type. Allowed types: ${allowedTypes.join(
          ', ',
        )}`,
      )
      return
    }

    if (!isValidFileSize(file, maxSizeInMB)) {
      errors.push(`${file.name}: File size exceeds ${maxSizeInMB}MB limit`)
      return
    }

    validFiles.push(file)
  })

  return {
    valid: validFiles.length > 0 && errors.length === 0,
    validFiles,
    errors,
  }
}

/**
 * Checks if file is an image
 * @param {File} file - The file to check
 * @returns {boolean} - True if file is an image
 */
export const isImageFile = (file) => {
  if (!file) return false
  return file.type.startsWith('image/')
}

/**
 * Checks if file is a PDF
 * @param {File} file - The file to check
 * @returns {boolean} - True if file is a PDF
 */
export const isPdfFile = (file) => {
  if (!file) return false
  return file.type === 'application/pdf'
}

/**
 * Gets file extension from filename
 * @param {string} fileName - The filename
 * @returns {string} - File extension (without dot)
 */
export const getFileExtension = (fileName) => {
  if (!fileName) return ''
  const lastDot = fileName.lastIndexOf('.')
  return lastDot > 0 ? fileName.substring(lastDot + 1).toLowerCase() : ''
}

/**
 * Formats file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}
